import { Component, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { merge } from 'rxjs';
import { Company, User } from '../../../auth/models';
import { ToastService } from '../../../main/services';
import { Tender } from '../../models';
import { CoverLetter, Document, RequestedDocument } from '../../../common/models';
import { TenderService } from '../../services';
import { CoverLetterService, TenderDocumentsService } from '../../../common/services';
import { TenderEditModalComponent } from './components/tender-edit-modal/tender-edit-modal.component';

@Component({
    selector: 'jft-tender-invitation',
    templateUrl: './tender-invitation.component.html',
    styleUrls: ['./tender-invitation.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class TenderInvitationComponent implements OnInit {

    public coverLetter: CoverLetter;
    public documents: Document[] = [];
    public requestedDocuments: RequestedDocument[] = [];
    public tenderId: string;
    public tender: Tender;

    constructor(
        private route: ActivatedRoute,
        private documentsService: TenderDocumentsService,
        private coverLetterService: CoverLetterService,
        private tenderService: TenderService,
        private ngbModal: NgbModal,
        private toast: ToastService,
        public company: Company,
        public user: User,
    ) {
    }

    public ngOnInit() {
        let data = this.route.snapshot.data;
        let parent = this.route.parent.snapshot;
        this.coverLetter = data['coverLetter'];
        this.documents = data['documents'];
        this.requestedDocuments = data['requestedDocuments'] || [];
        this.tenderId = parent.params.id;
        this.tender = parent.data['tender'];
    }

    // Editable while the tender runs; once it is over the list is a record.
    public get requestedDocumentsReadonly(): boolean {
        return !this.user.canManage(this.tender) || this.tender.isDone();
    }

    public updateDocumentsEvent($emit) {
        // Handle document deletion
        if ($emit && $emit.id) {
            this.deleteDocument($emit);
        } else {
            // Just refresh the documents
            this.updateDocuments();
            this.updateCoverLetter();
        }
    }

    private deleteDocument(document: Document) {
        // Check if it's a cover letter file
        if (this.coverLetter && this.coverLetter.file && this.coverLetter.file.id === document.id) {
            this.coverLetterService.removeDocument(this.tender, this.coverLetter, document).subscribe(() => {
                this.toast.success(`Cover letter file: ${document.name} removed`);
                this.updateCoverLetter();
            }, (e) => {
                this.toast.error(e);
            });
        } else {
            // It's a regular document
            this.documentsService.remove(this.tender, document).subscribe(() => {
                this.toast.success(`Document: ${document.name} removed`);
                this.updateDocuments();
            }, (e) => {
                this.toast.error(e);
            });
        }
    }

    public updateDocuments() {
        this.documentsService.fetch(this.tenderId).subscribe((document: Document[]) => {
            this.documents = document;
        })
    }

    public updateCoverLetter() {
        this.coverLetterService.fetch(this.tenderId).subscribe((document: CoverLetter) => {
            this.coverLetter = document;
        })
    }

    public openEditModal() {
        const modalRef = this.ngbModal.open(TenderEditModalComponent, {
            size: 'lg',
            backdrop: 'static'
        });

        modalRef.componentInstance.tender = this.tender;
        modalRef.componentInstance.coverLetter = this.coverLetter;

        modalRef.result.then((result) => {
            if (result) {
                // Update the tender and cover letter data
                this.tender = result.tender;
                this.coverLetter = result.coverLetter;
            }
        }, (dismissed) => {
            // Modal was dismissed
        });
    }

    public getAllDocuments(): Document[] {
        const allDocs = [];

        // Add cover letter if exists
        if (this.coverLetter && this.coverLetter.file) {
            allDocs.push(this.coverLetter.file);
        }

        // Add documents
        if (this.documents && this.documents.length) {
            allDocs.push(...this.documents);
        }

        return allDocs;
    }

    public addDocumentFiles($event: any): void {
        const files = $event.target.files;
        if (!files.length) {
            return;
        }

        const observables = [];
        for (let i = 0; i < files.length; i++) {
            const file = files[i];
            observables.push(this.documentsService.create(this.tender, file));
        }

        merge(...observables).subscribe((results) => {
            // Handle successful uploads
            this.updateDocuments();
            for (let i = 0; i < files.length; i++) {
                this.toast.success(`Document: ${files[i].name} uploaded successfully`);
            }
        }, (e) => {
            this.toast.error(e);
        });

        // Clear the input
        $event.target.value = '';
    }

    public downloadAllFiles(): void {
        const allDocs = this.getAllDocuments();

        if (allDocs.length === 0) {
            this.toast.info('No files to download');
            return;
        }

        // Download each file
        allDocs.forEach(doc => {
            this.documentsService.fetchFile(this.tenderId, doc);
        });

        this.toast.success(`Downloading ${allDocs.length} file(s)`);
    }

}
